/**
 * Booking pages (contracts booking.ts; Google Calendar appointment schedules): which start times a page offers, the
 * checks on its settings, and the labels the editor and the public page show. The API computes slots with
 * `bookableSlots` from the owner's busy times; the web and the phones only render them.
 */
import type { BookingLocation, BookingPageSettings, BookingQuestion, DateHours, TimeRange, WeeklyHours } from "@bundle/contracts";
import { msg, type MessageDescriptor } from "../i18n.ts";
import { MINUTE, addDays, fromLocal, localParts, startOfWeek } from "./calendar.ts";

/** Slots one query may cover (the public page asks a week or a month at a time). */
export const MAX_SLOT_RANGE_DAYS = 42;

/** "HH:mm" → minutes after midnight ("24:00" = 1440). */
export function clockMinutes(t: string): number {
  const [h, m] = t.split(":");
  return Number(h) * 60 + Number(m);
}

/** Minutes after midnight → "HH:mm". */
export function clockTime(minutes: number): string {
  const m = Math.max(0, Math.min(1440, Math.round(minutes)));
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
}

/** Google's starting hours: weekdays 9:00–17:00, weekends unavailable. */
export function defaultWeeklyHours(): WeeklyHours {
  const day = [{ start: "09:00", end: "17:00" }];
  return [[], day, day, day, day, day, []];
}

/** A new page's settings (the editor's starting point). */
export function defaultBookingPage(timeZone: string): BookingPageSettings {
  return {
    title: "",
    durationMinutes: 30,
    timeZone,
    weekly: defaultWeeklyHours(),
    dateHours: [],
    maxDaysAhead: 60,
    minNoticeMinutes: 240,
    bufferMinutes: 0,
    maxPerDay: null,
    guestsCanInvite: false,
    checkCalendars: true,
    location: { kind: "bundle_meeting" },
    questions: [],
    verifyEmail: false,
    reminders: [1440, 60],
  };
}

export type RangeProblem = "endBeforeStart" | "overlap";

/** What's wrong with one day's ranges, per range (undefined = fine). */
export function rangeProblems(ranges: readonly TimeRange[]): (RangeProblem | undefined)[] {
  const out: (RangeProblem | undefined)[] = ranges.map((r) => (clockMinutes(r.end) <= clockMinutes(r.start) ? "endBeforeStart" : undefined));
  const order = ranges.map((r, i) => ({ i, s: clockMinutes(r.start), e: clockMinutes(r.end) })).sort((a, b) => a.s - b.s);
  for (let k = 1; k < order.length; k++) {
    const prev = order[k - 1]!;
    const cur = order[k]!;
    if (cur.s < prev.e && out[cur.i] === undefined) out[cur.i] = "overlap";
  }
  return out;
}

/** Problems with a page's settings (the editor blocks Save on any; the API rejects them). */
export function bookingPageProblems(p: Pick<BookingPageSettings, "title" | "weekly" | "dateHours" | "durationMinutes">): ("noTitle" | "badHours" | "noHours")[] {
  const out: ("noTitle" | "badHours" | "noHours")[] = [];
  if (p.title.trim() === "") out.push("noTitle");
  const days = [...p.weekly, ...(p.dateHours ?? []).map((d) => d.ranges)];
  if (days.some((d) => rangeProblems(d).some((x) => x !== undefined))) out.push("badHours");
  if (!p.weekly.some((d) => d.some((r) => clockMinutes(r.end) - clockMinutes(r.start) >= p.durationMinutes))) out.push("noHours");
  return out;
}

/** Copy one weekday's ranges to every day (the editor's "Copy times to all days"). */
export function copyToAllDays(weekly: WeeklyHours, from: number): WeeklyHours {
  const src = weekly[from] ?? [];
  return weekly.map(() => src.map((r) => ({ ...r })));
}

/** The date key "YYYY-MM-DD" of the local day containing `ts`. */
export function dateKey(ts: number, timeZone: string): string {
  const p = localParts(ts, timeZone);
  return `${p.year}-${String(p.month).padStart(2, "0")}-${String(p.day).padStart(2, "0")}`;
}

/** A local day's open ranges: its date-specific hours when it has them, else the weekday's. */
export function hoursOn(dayStart: number, weekly: WeeklyHours, dateHours: readonly DateHours[], timeZone: string): readonly TimeRange[] {
  const key = dateKey(dayStart, timeZone);
  const special = dateHours.find((d) => d.date === key);
  if (special !== undefined) return special.ranges;
  return weekly[localParts(dayStart, timeZone).weekday] ?? [];
}

export type Interval = { start: number; end: number };

export type SlotInput = {
  page: Pick<BookingPageSettings, "durationMinutes" | "timeZone" | "weekly" | "dateHours" | "maxDaysAhead" | "minNoticeMinutes" | "bufferMinutes" | "maxPerDay">;
  /** The owner's busy times (events that block; their bookings included). */
  busy: readonly Interval[];
  /** This page's own confirmed bookings (they count toward `maxPerDay`; they are in `busy` too). */
  booked: readonly Interval[];
  from: number;
  to: number;
  now: number;
};

/**
 * The start times a page offers in [from, to): every `durationMinutes` from the start of each open range (Google
 * steps by the appointment length), whole appointments inside the range, at least `minNoticeMinutes` from now, before
 * the end of the `maxDaysAhead` window, not within `bufferMinutes` of a busy time, and only on days with fewer than
 * `maxPerDay` bookings. Sorted.
 */
export function bookableSlots(input: SlotInput): number[] {
  const { page, now } = input;
  const tz = page.timeZone;
  const dur = page.durationMinutes * MINUTE;
  const buffer = page.bufferMinutes * MINUTE;
  const earliest = Math.max(input.from, now + page.minNoticeMinutes * MINUTE);
  const latest = Math.min(input.to, page.maxDaysAhead === null ? Number.POSITIVE_INFINITY : addDays(now, page.maxDaysAhead + 1, tz));
  if (latest <= earliest) return [];
  const busy = [...input.busy].sort((a, b) => a.start - b.start);
  const out: number[] = [];
  for (let day = addDays(earliest, 0, tz); day < latest; day = addDays(day, 1, tz)) {
    const next = addDays(day, 1, tz);
    const max = page.maxPerDay ?? null;
    if (max !== null && input.booked.filter((b) => b.start >= day && b.start < next).length >= max) continue;
    const p = localParts(day, tz);
    const ranges = [...hoursOn(day, page.weekly, page.dateHours ?? [], tz)].sort((a, b) => clockMinutes(a.start) - clockMinutes(b.start));
    for (const r of ranges) {
      const rs = clockMinutes(r.start);
      const re = clockMinutes(r.end);
      for (let m = rs; m + page.durationMinutes <= re; m += page.durationMinutes) {
        const start = fromLocal(p.year, p.month, p.day, Math.floor(m / 60), m % 60, tz);
        const endMin = m + page.durationMinutes;
        const end = endMin >= 1440 ? next + (endMin - 1440) * MINUTE : fromLocal(p.year, p.month, p.day, Math.floor(endMin / 60), endMin % 60, tz);
        if (start < earliest || start >= latest || end - start !== dur) continue;
        if (busy.some((b) => b.start < end + buffer && b.end > start - buffer)) continue;
        if (out.length === 0 || out[out.length - 1]! < start) out.push(start);
      }
    }
  }
  return out;
}

/** Is `start` one of the page's slots right now (the API's check before it books or moves a booking)? */
export function isBookable(input: Omit<SlotInput, "from" | "to">, start: number): boolean {
  return bookableSlots({ ...input, from: start, to: start + input.page.durationMinutes * MINUTE }).includes(start);
}

/** The booked event's title: Google's "Intro call (Marina Costa)". */
export function bookedEventTitle(pageTitle: string, guestName: string): string {
  return `${pageTitle} (${guestName})`;
}

/** The booked event's description: the page's, then each answered question ("Company: Acme"). */
export function bookedEventDescription(description: string | undefined, answers: readonly { label: string; value: string }[]): string | undefined {
  const lines = answers.filter((a) => a.value.trim() !== "").map((a) => `${a.label}: ${a.value.trim()}`);
  const parts = [description?.trim() ?? "", lines.join("\n")].filter((s) => s !== "");
  return parts.length === 0 ? undefined : parts.join("\n\n");
}

/** "30 min", "1 h", "1 h 30 min" (the page's duration on rows and the public page). */
export function durationLabel(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
}

/** The duration choices the editor offers (Google's list), plus the page's own when it's custom. */
export function durationChoices(current?: number): number[] {
  const base = [15, 30, 45, 60, 90, 120];
  return current === undefined || base.includes(current) ? base : [...base, current].sort((a, b) => a - b);
}

/** Starts grouped by the local day they fall on in `timeZone` (the public page's columns), in order. */
export function slotsByDay(slots: readonly number[], timeZone: string): { day: number; slots: number[] }[] {
  const out: { day: number; slots: number[] }[] = [];
  for (const s of slots) {
    const p = localParts(s, timeZone);
    const day = fromLocal(p.year, p.month, p.day, 0, 0, timeZone);
    const last = out[out.length - 1];
    if (last !== undefined && last.day === day) last.slots.push(s);
    else out.push({ day, slots: [s] });
  }
  return out;
}

// ---------- Editor and public-page view logic ----------

/** Times the hour selects offer: every `step` minutes from 00:00 to 24:00 (Google's 15-minute steps). */
export function clockChoices(step = 15): string[] {
  const out: string[] = [];
  for (let m = 0; m <= 1440; m += step) out.push(clockTime(m));
  return out;
}

/**
 * The period "Add another period" appends to a day (Google: an hour after the day's last period, an hour long, kept
 * inside the day); an unavailable day gets 9:00–17:00.
 */
export function nextPeriod(ranges: readonly TimeRange[]): TimeRange {
  if (ranges.length === 0) return { start: "09:00", end: "17:00" };
  const last = Math.max(...ranges.map((r) => clockMinutes(r.end)));
  const start = Math.min(last + 60, 1380);
  return { start: clockTime(start), end: clockTime(Math.min(start + 60, 1440)) };
}

/** `weekly` with `day`'s ranges replaced (the other days untouched). */
export function withDay(weekly: WeeklyHours, day: number, ranges: readonly TimeRange[]): WeeklyHours {
  return weekly.map((r, i) => (i === day ? ranges.map((x) => ({ ...x })) : r));
}

/** The instant of wall-clock `t` ("HH:mm", "24:00" = the next midnight) on the local day starting at `dayStart`. */
function clockOn(dayStart: number, t: string, timeZone: string): number {
  const m = clockMinutes(t);
  if (m >= 1440) return addDays(dayStart, 1, timeZone);
  const p = localParts(dayStart, timeZone);
  return fromLocal(p.year, p.month, p.day, Math.floor(m / 60), m % 60, timeZone);
}

/**
 * The editor's live preview: the page's open periods on each local day of `days` (day starts in `timeZone`, the
 * viewer's zone), as instants clipped to the day. The page's own zone decides the wall clock, so a page in São Paulo
 * previewed from New York shifts.
 */
export function availabilityBlocks(
  page: Pick<BookingPageSettings, "timeZone" | "weekly" | "dateHours">,
  days: readonly number[],
  timeZone: string,
): { day: number; blocks: Interval[] }[] {
  const tz = page.timeZone;
  return days.map((day) => {
    const end = addDays(day, 1, timeZone);
    const blocks: Interval[] = [];
    // Every page-zone day that overlaps this viewer day (the zones may straddle it).
    for (let d = addDays(day - 86_400_000, 0, tz); d < end; d = addDays(d, 1, tz)) {
      for (const r of hoursOn(d, page.weekly, page.dateHours ?? [], tz)) {
        const s = Math.max(clockOn(d, r.start, tz), day);
        const e = Math.min(clockOn(d, r.end, tz), end);
        if (e > s) blocks.push({ start: s, end: e });
      }
    }
    blocks.sort((a, b) => a.start - b.start);
    return { day, blocks };
  });
}

/** What a booking page row says under its title: "Paused" while paused, the duration, and where. */
export function bookingPageSummary(p: Pick<BookingPageSettings, "durationMinutes" | "location"> & { paused?: boolean }): { paused: boolean; duration: string; location: BookingLocation["kind"] } {
  return { paused: p.paused === true, duration: durationLabel(p.durationMinutes), location: p.location.kind };
}

/** Where an appointment happens, as rows and the public page name it ("Bundle meeting", "In person", …). */
export function locationLabel(kind: BookingLocation["kind"]): MessageDescriptor {
  switch (kind) {
    case "bundle_meeting":
      return msg("booking.location.bundleMeeting");
    case "in_person":
      return msg("booking.location.inPerson");
    case "phone":
      return msg("booking.location.phone");
    case "none":
      return msg("booking.location.none");
  }
}

/** The settings to send (create/update): title and questions trimmed, blank questions and an empty description dropped. */
export function bookingPageRequest(p: BookingPageSettings): BookingPageSettings {
  const out: BookingPageSettings = { ...p, title: p.title.trim() };
  const description = p.description?.trim() ?? "";
  if (description === "") delete out.description;
  else out.description = description;
  out.questions = p.questions.filter((q) => q.label.trim() !== "").map((q) => ({ ...q, label: q.label.trim() }));
  return out;
}

/** A new form question's id: the first unused of "q1", "q2", … */
export function nextQuestionId(questions: readonly { id: string }[]): string {
  for (let n = 1; ; n++) if (!questions.some((q) => q.id === `q${n}`)) return `q${n}`;
}

/** The public picker's week: `days` local days from the start of the week containing `anchor`, each with its open starts. */
export function slotWeek(anchor: number, slots: readonly number[], timeZone: string, days = 7, firstDay = 0): { day: number; slots: number[] }[] {
  const start = startOfWeek(anchor, timeZone, firstDay);
  const out: { day: number; slots: number[] }[] = [];
  for (let i = 0; i < days; i++) {
    const day = addDays(start, i, timeZone);
    const next = addDays(day, 1, timeZone);
    out.push({ day, slots: slots.filter((s) => s >= day && s < next) });
  }
  return out;
}

export type BookFormField = "firstName" | "lastName" | "email" | (string & {});

/** The guest form's problems: required fields still empty or a malformed email (question ids for required questions). */
export function bookFormProblems(
  form: { firstName: string; lastName: string; email: string; answers: Readonly<Record<string, string>> },
  questions: readonly BookingQuestion[],
): BookFormField[] {
  const out: BookFormField[] = [];
  if (form.firstName.trim() === "") out.push("firstName");
  if (form.lastName.trim() === "") out.push("lastName");
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) out.push("email");
  for (const q of questions) if (q.required && (form.answers[q.id] ?? "").trim() === "") out.push(q.id);
  return out;
}
